<template>
  <div id="app" :class="[routeClass, { 'app-noauth': !isAuthenticated }]">
    <LoadingSpinner v-if="isAuthenticated && loading" />
    <div v-if="isAuthenticated && !loading" class="main-app container-fluid">
      <portal-target name="imgviewer-modal" class="imgviewer-portal-modal" />
      <portal-target name="dash-modal" />
      <portal-target name="aux-modal" />
      <PremiumModal v-if="$store.getters.getPremiumModalState" />
      <OnboardingWizardModal v-if="isOnboardingWizardOpen" />
      <PremiumCongratsModal v-if="isPremiumCongratsOpen" />

      <DashboardModal v-if="!isOnboardingOpen && isModalOpen" />
      <OnboardingModal v-if="isOnboardingOpen" />
      <HoursTimer ref="hours_timer" />
      <div
        class="row topbar-wrapper topbar-wrapper--fixed"
        :style="'background-color:' + brandingColor + '!important;'"
      >
        <GekkoMenu @toggleChatbot="openChatbot = !openChatbot" />
      </div>
      <div class="row main-row position-relative" :class="routeClass">
        <div v-if="currentUser.profile && currentUser.profile.deletion_procedure" class="w-100 content-wrap pt-3">
          <DeleteAccountBanner />
        </div>

        <router-view class="w-100 content-wrap" :class="routeClass" @startTimer="$refs.hours_timer.newTimer()" />
      </div>
      <ChatbotSidebar :show="openChatbot" @close="openChatbot = false" />
    </div>
    <div v-if="!isAuthenticated && !loading" class="container-fluid">
      <router-view class="w-100 content-wrap" :class="routeClass" @authorised="authorise" />
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import { adjustColor, bwFor } from '@/utils/'

import mixpanel from '@/mixins/mixpanel'

import GekkoMenu from '@/components/GekkoMenu'
import PremiumModal from '@/components/PremiumModal.vue'
import HoursTimer from '@/components/hours/HoursTimer.vue'
import DashboardModal from '@/components/dashboard/ui/DashboardModal'
import OnboardingModal from '@/components/OnboardingModal'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'
import ChatbotSidebar from '@/components/ui/ChatbotSidebar'
import DeleteAccountBanner from '@/components/dashboard/ui/DeleteAccountBanner.vue'
import OnboardingWizardModal from '@/components/OnboardingWizardModal'
import PremiumCongratsModal from '@/components/ui/PremiumCongratsModal.vue'

export default {
  name: 'Gekko',
  mixins: [mixpanel],
  components: {
    GekkoMenu,
    PremiumModal,
    HoursTimer,
    DashboardModal,
    OnboardingModal,
    LoadingSpinner,
    ChatbotSidebar,
    DeleteAccountBanner,
    OnboardingWizardModal,
    PremiumCongratsModal,
  },
  data() {
    return {
      loading: true,
      nonBlockingRoutes: [
        'EditCost',
        'AddCost',
        'AddContact',
        'EditProject',
        'AddProject',
        'EditContact',
        'EditTrip',
        'AddTrip',
        'CostTypes',
        'BalanceSheetEditCost',
        'AddInvestmentItem',
        'EditInvestmentItem',
      ],
      openChatbot: false,
      loadedPlugins: [],
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (!this.loading && this.isAuthenticated) {
          this.privatePageLoad()
        }
        this.genericPageLoad()

        document.title = to.meta.title + ' - Gekko'
        document.body.classList.remove('modal-on')
      },
    },
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/getAuthenticated',
      isOnboardingOpen: 'onboarding/isOnboardingOpen',
      isOnboardingWizardOpen: 'onboarding/isOnboardingWizardOpen',
      isPremiumCongratsOpen: 'onboarding/isPremiumCongratsOpen',
      isModalOpen: 'dashboard/isModalOpen',
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      getUserId: 'main/getUserId',
      preferredLanguage: 'main/getPreferredLanguage',
      getMobileAppView: 'main/getMobileAppView',
      getCompanyId: 'main/getCompanyId',
      isVisiting: 'main/isVisiting',
    }),
    brandingColor() {
      if (this.currentCompany.accountant_branding) {
        return this.currentCompany.accountant_branding.color
      } else {
        return '#22AC8A' // $colorBrand
      }
    },
    routeClass() {
      return this.$route && this.$route.name ? this.$route.name.toLowerCase() : ''
    },
  },
  created() {
    this.$store.commit('setRoot', this.$root)
    this.loadSiteConfig().then((urlsObj) => {
      window.Urls = urlsObj
      this.$root.constructor.prototype.Urls = urlsObj
      this.checkAuth().then(() => {
        this.initApp()
      })
    })
  },
  destroyed() {
    this.unsubscribe()
  },
  mounted() {
    this.attachMixpanelClickEvents()
  },
  methods: {
    ...mapMutations({
      resetCustomers: 'customers/RESET_STATE',
      loadVisitingCookie: 'main/LOAD_VISITING_COOKIE',
    }),
    ...mapActions({
      loadSiteConfig: 'main/loadSiteConfig',
      loadMainGlobalData: 'main/loadMainGlobalData',
      loadSubscriptions: 'subscriptionStore/loadSubscriptionsMainData',
      checkAuth: 'auth/checkAuth',
      switchToVisitingCompany: 'main/switchToVisitingCompany',
    }),
    authorise() {
      this.loading = true;
      this.checkAuth().then(() => {
        this.initApp()
      })
    },
    initApp() {
      // console.log('Gekko.vue::initApp(). Authenticated ->', this.isAuthenticated)
      if (this.isAuthenticated) {
        // TODO FIX THIS
        const queryParams = this.getQueryParams()
        this.loadVisitingCookie()
        const vc_id = queryParams.vc_id ?? this.isVisiting

        if (vc_id) {
          this.switchToVisitingCompany(vc_id)
          // this.$router.push({ name: 'Dashboard', params: { language: this.preferredLanguage } })
        }

        this.setupSentry()
        this.setupData()
      } else {
        this.loading = false
        this.$language.current = this.$route.params.language
      }
    },
    // TODO FIX/REMOVE THIS fn
    getQueryParams() {
      const query = window.location.search.substring(1)
      const params = {}

      query.split('&').forEach((param) => {
        const [key, value] = param.split('=')
        if (key) {
          params[key] = decodeURIComponent(value)
        }
      })

      return params
    },
    resetStates() {
      this.resetCustomers()
    },
    setupData() {
      // this.loadSiteConfig().then((urlsObj) => {
      //   window.Urls = urlsObj
      //   this.$root.constructor.prototype.Urls = urlsObj

        this.loadMainGlobalData().then(() => {
          // mixpanel: first time we send this event here, afterwards on route watch
          this.privatePageLoad()

          this.afterSetupAppConfig()
          this.$store.commit('setIsPremium', this.currentCompany.is_premium)
          this.$store.commit('main/SET_CONSTANTS')
          if (this.currentCompany.name != null && !this.currentCompany.name.length) {
            this.$store.commit('toggleCompanyModal')
          }
          if (!this.unsubscribe) {
            this.subscribeToMutations()
          }
          // TODO WTF FIX THIS
          const isAccountant = this.currentUser.is_accountant
          const isOnExportPage = this.$route.path.includes('/download/export-file/')
          const isAtDefaultCompany = this.getCompanyId == this.currentUser.default_company
          const redirectPath = this.$route.query.redirect

          if (isAccountant) {
            if (!isOnExportPage && isAtDefaultCompany && !this.isVisiting) {
              const targetPath = `/${this.preferredLanguage}/accountants/dashboard/`
              window.location.href = targetPath
            }
            this.loading = false
          } else {
            if (redirectPath) {
              this.$router.push(redirectPath)
            }
            this.loading = false
          }

          this.setupBrandingColors()
        })
        this.loadSubscriptions()
      // })
    },
    setupBrandingColors() {
      const lightBgColor = adjustColor(this.brandingColor, { lightness: 0.7, gray: 0, perceptual: true })
      const topbarBgColor = adjustColor(this.brandingColor, { lightness: 0.9, gray: 0.3, perceptual: true })
      // const submenuBgColor = adjustColor(newBrandingColor, { lightness: 0.8, gray: 0.5, perceptual: true })
      const submenuBgColor = '#f5f5f5'
      const textHLColor = bwFor(this.brandingColor)

      this.gkLogoColor = textHLColor === '#FFFFFF' ? 'white' : 'brand'

      document.documentElement.style.setProperty('--branding-color', this.brandingColor)
      document.documentElement.style.setProperty('--light-branding-color', lightBgColor)
      document.documentElement.style.setProperty('--submenu-background-color', submenuBgColor)
      document.documentElement.style.setProperty('--text-hl-branding-color', textHLColor)
      document.documentElement.style.setProperty('--text-hl-branding-color-hover', textHLColor + 'b3')
      document.documentElement.style.setProperty('--topbar-background-color', topbarBgColor)
    },
    subscribeToMutations() {
      // console.log('Gekko.vue::subscribeToMutations()')

      this.unsubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type === 'auth/SET_AUTHENTICATED') {
          // console.log('Gekko.vue::created() -> mutation auth/SET_AUTHENTICATED', this.isAuthenticated)
          if (this.isAuthenticated) {
            this.initApp()
          }
        }
        if (mutation.type === 'main/SET_COMPANY_ID') {
          this.loading = true
          this.resetStates()
          this.setupData()
        }
      })
    },
    afterSetupAppConfig() {
      // console.log('Gekko.vue::afterSetupAppConfig()')

      document.documentElement.lang = this.preferredLanguage
      this.$language.current = this.preferredLanguage

      this.setupFirebase()

      if (!this.getMobileAppView) {
        this.loadFreshworks()
      }

      this.loadDateJS()
    },
    setupSentry() {
      if (this.$sentry) {
        this.$sentry.configureScope((scope) => {
          scope.setUser({ id: this.getUserId })
        })
      }
    },
    setupFirebase() {
      // console.log('Gekko.vue::setupFirebase()')
      if (window.fbAnalytics) {
        window.fbAnalytics.setUserId(this.currentUser.id)
        window.fbAnalytics.setUserProperties({
          email: this.currentUser.email,
          id: this.currentUser.id,
          premium: this.currentUser.is_premium,
        })
      }
    },
    attachMixpanelClickEvents() {
      // TODO refactor mixpanel integration
      // TODO fix setTimeout inconsistent hack used to wait for all [data-analytics-click] elements to be mounted across all subcomponents
      setTimeout(() => {
        if (window.mixpanel) {
          var queryElements = document.querySelectorAll('[data-analytics-click]')
          for (let i = 0; i < queryElements.length; i++) {
            const target_name = queryElements[i].getAttribute('data-analytics-click')
            queryElements[i].addEventListener('click', () => {
              window.mixpanel.track('click', {
                target: target_name,
                url: this.$route.path,
                referrer: document.referrer,
                'is logged in': true,
              })
            })
          }
        }
      }, 1000)
    },
    loadFreshworks() {
      // console.log('Gekko::loadFreshworks()')
      const widget_id = 47000002881

      window.fwSettings = { widget_id: widget_id, locale: this.preferredLanguage }
      !(function () {
        if ('function' != typeof window.FreshworksWidget) {
          var n = function () {
            n.q.push(arguments)
          }
          ;(n.q = []), (window.FreshworksWidget = n)
        }
      })()

      window.FreshworksWidget('setLabels', {
        en: {
          banner: 'How can we help you?',
          launcher: 'Help',
          contact_form: {
            title: 'Contact Us',
            submit: 'Send',
            confirmation: 'Thank you for your message, we will reach back to you shortly.',
          },
        },
        es: {
          banner: '¿Cómo podemos ayudarte?',
          launcher: 'Ayuda',
          contact_form: {
            title: 'Contáctanos',
            submit: 'Enviar',
            confirmation: 'Gracias por tu mensaje, nos pondremos en contacto en breve.',
          },
        },
        nl: {
          banner: 'Hoe kunnen wij je vandaag helpen?',
          launcher: 'Help',
          contact_form: {
            title: 'Neem contact met ons op',
            submit: 'Verstuur',
            confirmation: 'Bedankt voor je bericht, we zullen hier snel op reageren',
          },
        },
      })
      window.FreshworksWidget('prefill', 'ticketForm', {
        name: this.currentUser.profile ? this.currentUser.profile.full_name : '',
        email: this.currentUser.email,
        custom_fields: {
          cf_language: this.preferredLanguage.toUpperCase(),
          cf_company_country: this.currentCompany.country,
          cf_user_id: this.currentUser.id,
          cf_company_profile_id: this.currentCompany.id,
          cf_premium: this.currentUser.is_premium,
        },
      })
      window.FreshworksWidget(
        'hide',
        'ticketForm',
        [
          'custom_fields.cf_language',
          'custom_fields.cf_company_country',
          'custom_fields.cf_premium',
          'custom_fields.cf_user_id',
          'custom_fields.cf_company_profile_id',
        ],
        {}
      )

      if (!this.loadedPlugins.includes('freshworks')) {
        const script = document.createElement('script')
        script.src = 'https://widget.freshworks.com/widgets/' + widget_id + '.js'
        script.onload = () => {
          // console.log('Gekko::loadFreshworks() -> Freshworks loaded')
        }
        script.onerror = () => {
          console.error('Gekko::loadFreshworks() -> Failed to load Freshworks script.')
          this.loadedPlugins = this.loadedPlugins.filter((plugin) => plugin !== 'freshworks')
        }
        this.loadedPlugins.push('freshworks')
        document.body.appendChild(script)
      }
    },
    loadDateJS() {
      const i18n = Date && Date.i18n
      if (!i18n || typeof i18n.setLanguage !== 'function') {
        console.warn('[DateJS] i18n.setLanguage not available')
        return
      }

      if (this.preferredLanguage === 'nl') {
        i18n.setLanguage('nl-NL')
      } else if (this.preferredLanguage === 'es') {
        i18n.setLanguage('es-ES')
      }
    },
  },
}
</script>

<style lang="scss">
.main-app {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.topbar-wrapper {
  &--fixed {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1005;
  }
}
.topbar-wrapper--fixed + .main-row {
  margin-top: $topbarHeight;
  padding-bottom: 4rem;
}
.main-row,
.main-row .content-wrap {
  flex-direction: column;
  flex: 1;
  &.hours {
    display: flex;
  }
  > .content {
    height: 100%;
    padding: 0 1rem;
    .content--inner {
      border: 1px solid $colorBorderDash;
      border-radius: $radiusLg;
      overflow: hidden;
      overflow-x: auto;
      box-shadow: $contentShadow;
    }
  }
}
.vue-portal-target {
  z-index: 1100;
}
.imgviewer-portal-modal {
  z-index: 1200;
}
@media (max-width: '767px') {
  .main-row,
  .main-row .content-wrap {
    > .content {
      padding: 0 0.5rem;
    }
  }
}

.app-noauth {
  align-items: center;
  padding-top: 3rem;

  background: linear-gradient(-7deg, #fff, #fff 50%, $colorBrand calc(50% + 1px), #1e8369 100%);
  background-size: 100% 700px;
  background-repeat: no-repeat;

  &.signupheroes {
    background-size: 100% 1200px;
  }
  &.signupaccountants {
    background: linear-gradient(-7deg, #fff, #fff 50%, $colorAccountants calc(50% + 1px), #8c243d 100%);
  }
}
</style>
